import { useEffect, useRef, useState } from 'react';
import type { JobThreadWithAcl } from '../hooks/useThreads';
import styles from './JobThread.module.css';

interface JobThreadItemProps {
  thread: JobThreadWithAcl;
  active: boolean;
  onClick: () => void;
  onDelete?: (sessionKey: string) => void;
  onShare?: (sessionKey: string) => void;
  onRename?: (sessionKey: string) => void;
}

const VISIBILITY_ICON: Record<string, string> = {
  private: '\u{1F464}',  // 👤
  shared:  '\u{1F465}',  // 👥
  org:     '\u{1F310}',  // 🌐
};

const VISIBILITY_TITLE: Record<string, string> = {
  private: 'Private — only you',
  shared:  'Shared with specific users',
  org:     'Shared with the whole team',
};

export function JobThreadItem({ thread, active, onClick, onDelete, onShare, onRename }: JobThreadItemProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const chip = (
    <span
      className={`${styles.visChip} ${styles[`vis_${thread.visibility}`]}`}
      title={VISIBILITY_TITLE[thread.visibility]}
    >
      <span aria-hidden>{VISIBILITY_ICON[thread.visibility]}</span>
      {thread.visibility === 'shared' && thread.shareCount > 0 && (
        <span className={styles.visCount}>+{thread.shareCount}</span>
      )}
    </span>
  );

  const showMenuBtn = thread.ownedByMe && (onDelete || onShare || onRename);

  return (
    <div
      ref={wrapRef}
      className={`${styles.thread} ${active ? styles.active : ''}`}
      onClick={onClick}
    >
      <div className={styles.header}>
        <span className={`${styles.name} ${thread.unread > 0 ? styles.unread : ''}`}>
          {thread.name}
        </span>
        {thread.ownedByMe && onRename && (
          <button
            type="button"
            className={styles.pencilBtn}
            aria-label="Rename thread"
            title="Rename"
            onClick={(e) => {
              e.stopPropagation();
              onRename(thread.id);
            }}
          >
            {'✎'}
          </button>
        )}
        {chip}
        {showMenuBtn && (
          <button
            type="button"
            className={styles.menuBtn}
            aria-label="Thread actions"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
          >
            {'\u22EF'}
          </button>
        )}
      </div>
      <div className={styles.meta}>
        <code className={styles.code}>{thread.code}</code>
      </div>
      {thread.lastMessage && <div className={styles.preview}>{thread.lastMessage}</div>}

      {menuOpen && (
        <div className={styles.menu} onClick={(e) => e.stopPropagation()}>
          {onRename && (
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => { setMenuOpen(false); onRename(thread.id); }}
            >
              Rename…
            </button>
          )}
          {onShare && (
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => { setMenuOpen(false); onShare(thread.id); }}
            >
              Share…
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              className={`${styles.menuItem} ${styles.menuItemDanger}`}
              onClick={() => {
                setMenuOpen(false);
                if (confirm(`Delete "${thread.name}"? Other shared users lose access.`)) {
                  onDelete(thread.id);
                }
              }}
            >
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}
